import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import Invoice from '~/types/Invoice';
import {
  extractRequestable,
  getFailed,
  getNotRequested,
  getRefreshFailed,
  getRefreshing,
  getRefreshSuccess,
  getRequesting,
  getSuccess,
} from '~/utils/fetchable';

export type InvoicesState = {
  readonly byObjectId: HashMap<string, Refreshable<Invoice, string>>;
  readonly invoices: Requestable<Refreshable<Invoice, string>[], string>;
  readonly displayInvoice: Requestable<Invoice, string>; // Used to display invoice in invoice download page
  readonly numPages: number;
  readonly page: number;
};

const initialState: InvoicesState = {
  byObjectId: {},
  invoices: getNotRequested(),
  displayInvoice: getNotRequested(),
  numPages: 1,
  page: 1,
};

// replaces a refreshable invoice in both byObjectId and invoices
function replaceInvoice(
  state: InvoicesState,
  objectId: string,
  oldValue: Refreshable<Invoice, string>,
  newValue: Refreshable<Invoice, string>,
) {
  if (!objectId) {
    return state;
  }

  let { byObjectId, invoices } = state;

  byObjectId = {
    ...byObjectId,
    [objectId]: newValue,
  };

  const oldInvoices = extractRequestable(invoices);
  if (oldInvoices) {
    const newInvoices = [...oldInvoices];
    newInvoices.splice(newInvoices.indexOf(oldValue), 1, newValue);
    invoices = getSuccess(newInvoices);
  }

  return {
    ...state,
    byObjectId,
    invoices,
  };
}

function invoicesReducer(state: InvoicesState = initialState, action: ActionTypes) {
  switch (action.type) {
    case ActionKeys.INVOICE_CHARGE: {
      const { objectId } = action.payload;
      const oldValue = state.byObjectId[objectId] || getNotRequested();
      const newValue = getRefreshing(oldValue);
      return replaceInvoice(state, objectId, oldValue, newValue);
    }

    case ActionKeys.INVOICE_CHARGE_SUCCESS: {
      const objectId = action.payload.invoice.objectId;
      if (!objectId) return state;
      const oldValue = state.byObjectId[objectId] || getNotRequested();
      const newValue = getRefreshSuccess(oldValue, action.payload.invoice);
      return replaceInvoice(state, objectId, oldValue, newValue);
    }

    case ActionKeys.INVOICE_CHARGE_ERROR: {
      const { objectId, err } = action.payload;
      const oldValue = state.byObjectId[objectId] || getNotRequested();
      const newValue = getRefreshFailed(oldValue, err.message);
      return replaceInvoice(state, objectId, oldValue, newValue);
    }

    case ActionKeys.INVOICES_FETCH: {
      return {
        ...state,
        byObjectId: {},
        invoices: getRequesting(),
      };
    }

    case ActionKeys.INVOICES_FETCH_SUCCESS: {
      const { invoices, page, numPages } = action.payload;
      const byObjectId: Record<string, Refreshable<Invoice, string>> = {};
      const refreshableInvoices: Refreshable<Invoice, string>[] = invoices.map((invoice) => {
        const refreshableInvoice: Refreshable<Invoice, string> = getRefreshSuccess(
          getNotRequested(),
          invoice,
        );
        if (invoice.objectId) {
          byObjectId[invoice.objectId] = refreshableInvoice;
        }
        return refreshableInvoice;
      });

      return {
        ...state,
        byObjectId,
        numPages,
        page,
        invoices: getSuccess(refreshableInvoices),
      };
    }

    case ActionKeys.INVOICES_FETCH_ERROR: {
      return {
        ...state,
        byObjectId: {},
        invoices: getFailed(action.payload.err.message),
      };
    }

    case ActionKeys.INVOICE_FETCH: {
      return {
        ...state,
        displayInvoice: getRequesting(),
      };
    }

    case ActionKeys.INVOICE_FETCH_SUCCESS: {
      return {
        ...state,
        displayInvoice: getSuccess(action.payload.invoice),
      };
    }

    case ActionKeys.INVOICE_FETCH_ERROR: {
      return {
        ...state,
        displayInvoice: getFailed(action.payload.err.message),
      };
    }

    default: {
      return state;
    }
  }
}

export default invoicesReducer;
